import type { HekateDisplayForm } from '@onshape/www-inertia';
import { FieldHtmlNameUtil, Trans } from '@onshape/www-inertia';
import type { Ref } from 'vue';
import hekateBus from '../../../../../../bus';
import UnifiedSignupAgent from '../UnifiedSignupAgent';
import UnifiedSignupScreenComponent from '../UnifiedSignupScreenComponent';
import AbstractSignupScreen from './AbstractSignupScreen';
import UnifiedSignupEmailType from '../session-info/UnifiedSignupEmailType';
import UnifiedSignupUtil from '../UnifiedSignupUtil';
import type UnifiedSignupSessionInfo from '../session-info/UnifiedSignupSessionInfo';

export default abstract class AbstractStartSignupScreen extends AbstractSignupScreen {
  private submitText: string;

  constructor({
    sessionInfo,
    formDefinition,
    isBackButtonHidden,
    submitText,
  }: {
    sessionInfo: Ref<UnifiedSignupSessionInfo>;
    formDefinition: Ref<HekateDisplayForm>;
    isBackButtonHidden: Ref<boolean>;
    submitText: string;
  }) {
    super({ sessionInfo, formDefinition, isBackButtonHidden });
    this.submitText = submitText;
  }

  public getVisibleComponents() {
    const result = [
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.firstName),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.lastName),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.email),
      }),
    ];
    return result;
  }

  public getValidatedComponents(): UnifiedSignupScreenComponent[] {
    return this.getVisibleComponents();
  }

  public async toNextScreen(): Promise<boolean> {
    if (UnifiedSignupUtil.isForcedScreenEnabled()) {
      this.sessionInfo.value.emailCheckCode = 200;
      this.sessionInfo.value.emailType = UnifiedSignupEmailType.genEmail;
      return true;
    }

    const email = this.getField(FieldHtmlNameUtil.email);
    try {
      const result = await UnifiedSignupAgent.emailCheck({
        email: email.val as string,
      });
      if (![401, 200, 204, 422].includes(result.emailCheckCode)) {
        throw new Error(`Unexpected emailCheckCode: ${result.emailCheckCode}`);
      }
      this.getField(FieldHtmlNameUtil.cadCheckStatusCode).val = result.emailCheckCode;
      this.sessionInfo.value.emailCheckCode = result.emailCheckCode;
      this.sessionInfo.value.emailCheckErrorReason = result.errorReason || '';
      this.sessionInfo.value.emailType = result.emailType;
      this.sessionInfo.value.isDiscoEligible = result.isDiscoEligible;
      this.isBackButtonHidden.value = false;
    } catch (e) {
      console.error('Email check failed', e);
      this.getField(FieldHtmlNameUtil.cadCheckStatusCode).val = 500;
      this.sessionInfo.value.emailCheckCode = 500;
      this.sessionInfo.value.emailCheckErrorReason = 'Network or server error';
      this.sessionInfo.value.emailType = UnifiedSignupEmailType.genEmail; // Default to genEmail to allow users to proceed in case of error
    }
    return true;
  }

  public override async initWatchers(): Promise<void> {
    const formVariantField = this.getField('formVariant');

    // AB TESTING
    if (formVariantField.val === 'UssAbTest') {
      this.trackEvent('uss-ab-test');
      const email = this.getField(FieldHtmlNameUtil.email);
      email.label = Trans.byValue('Business Email');
    }

    hekateBus.dispatchEvent('lazy-load-images', {});
  }

  public override getNextButtonLabel(): string {
    return this.submitText || Trans.byValue('Get Started');
  }

  public getProgress(): number {
    return 0;
  }

  public override isInitialScreen(): boolean {
    return true;
  }
}
